import { useEffect, useId, useRef, useState } from 'react';
import type { MagixPose } from '../../ui/Magix.js';

/**
 * Magix, turned into something that looks back.
 *
 * The picture is a flat render, so the depth is borrowed: the whole figure is
 * tilted a few degrees toward the pointer, and the eyes, which are drawn here
 * on top of the visor instead of baked into the picture, slide further than
 * the head turns. A small patch of visor-black sits under them to cover the
 * eyes that are painted into the image.
 *
 * Everything that moves is a CSS variable written on one frame per tick, so
 * React never re-renders while the pointer moves, and nothing here reads
 * layout inside the tick. It follows the pointer with reduced motion on too:
 * the owner decided the landing moves the same for everybody, and a few
 * degrees of tilt under the reader's own pointer is not travel.
 */

const clamp = (n: number, lo = -1, hi = 1) => Math.min(hi, Math.max(lo, n));

/**
 * The face is drawn for `hello` alone: its eyes sit where that render's visor
 * is. Every other pose is its own picture, with eyes painted where its own
 * head is, so it keeps them and only the tilt follows the pointer.
 */
export function Mascot3D({
  className = '',
  priority = false,
  pose = 'hello',
}: {
  className?: string;
  priority?: boolean;
  pose?: 'hello' | MagixPose;
}) {
  // `:` is legal in an id but not inside `url(#…)`, which is where these two go.
  const id = `m3d${useId().replace(/:/g, '')}`;
  const rootRef = useRef<HTMLDivElement>(null);
  const figureRef = useRef<HTMLDivElement>(null);
  const faceRef = useRef<SVGSVGElement>(null);
  const [blink, setBlink] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    const figure = figureRef.current;
    const face = faceRef.current;
    if (!root || !figure || !face) return;

    const target = { x: 0, y: 0 };
    const cur = { x: 0, y: 0 };
    let raf = 0;
    let visible = true;
    const timers = new Set<number>();
    const later = (fn: () => void, ms: number) => {
      const t = window.setTimeout(() => {
        timers.delete(t);
        fn();
      }, ms);
      timers.add(t);
      return t;
    };

    const tick = () => {
      raf = 0;
      cur.x += (target.x - cur.x) * 0.12;
      cur.y += (target.y - cur.y) * 0.12;
      figure.style.setProperty('--rx', String(-cur.y * 5));
      figure.style.setProperty('--ry', String(cur.x * 7));
      face.style.setProperty('--ex', `${cur.x * 14}px`);
      face.style.setProperty('--ey', `${cur.y * 9}px`);
      root.style.setProperty('--sx', `${cur.x * -10}px`);
      // Settled: stop asking for frames until something moves the target again.
      if (Math.abs(target.x - cur.x) < 0.002 && Math.abs(target.y - cur.y) < 0.002) return;
      raf = requestAnimationFrame(tick);
    };
    const start = () => {
      if (!raf && visible && !document.hidden) raf = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    // The pointer's place on the whole window, centred on the figure: looking
    // left of it is the same as being left of it, however far the page is.
    const lookAt = (clientX: number, clientY: number) => {
      const r = root.getBoundingClientRect();
      target.x = clamp((clientX - (r.left + r.width / 2)) / (window.innerWidth / 2));
      target.y = clamp((clientY - (r.top + r.height / 2)) / (window.innerHeight / 2));
      start();
    };

    let touchBack = 0;
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      lookAt(e.clientX, e.clientY);
    };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      lookAt(e.clientX, e.clientY);
      window.clearTimeout(touchBack);
      touchBack = later(() => {
        target.x = 0;
        target.y = 0;
        start();
      }, 1800);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });

    // Nobody to follow on a touch screen between taps, so it glances about now and then.
    let idle = 0;
    if (window.matchMedia('(hover: none)').matches) {
      idle = window.setInterval(() => {
        if (!visible || document.hidden) return;
        target.x = (Math.random() - 0.5);
        target.y = (Math.random() - 0.5);
        start();
        later(() => {
          target.x = 0;
          target.y = 0;
          start();
        }, 900);
      }, 4500);
    }

    const io = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? true;
      if (visible) start();
      else stop();
    });
    io.observe(root);
    const onVisibility = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVisibility);

    let blinkTimer = 0;
    const scheduleBlink = () => {
      blinkTimer = later(
        () => {
          if (visible && !document.hidden) {
            setBlink(true);
            later(() => setBlink(false), 130);
          }
          scheduleBlink();
        },
        3500 + Math.random() * 3500,
      );
    };
    scheduleBlink();

    return () => {
      stop();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      document.removeEventListener('visibilitychange', onVisibility);
      io.disconnect();
      window.clearInterval(idle);
      window.clearTimeout(blinkTimer);
      timers.forEach((t) => window.clearTimeout(t));
      timers.clear();
    };
  }, []);

  return (
    <div className={`m3d ${className}`} ref={rootRef} aria-hidden="true" data-pose={pose}>
      <div className="m3d-figure" ref={figureRef}>
        <img
          // Keyed by pose so a new pose is a new element, which is what lets
          // a screen give each change of pose its own short arrival.
          key={pose}
          src={`/magix/${pose}.webp`}
          alt=""
          width={pose === 'hello' ? 512 : 384}
          height={pose === 'hello' ? 512 : 384}
          decoding="async"
          {...(priority ? { fetchPriority: 'high' as const } : { loading: 'lazy' as const })}
        />
        <svg
          className="m3d-face"
          viewBox="0 0 512 512"
          ref={faceRef}
          data-blink={blink ? 'true' : undefined}
          // Hidden rather than removed: the pointer effect holds this node.
          display={pose === 'hello' ? undefined : 'none'}
        >
          <defs>
            <filter id={`${id}-soft`} x="-20%" y="-40%" width="140%" height="180%">
              <feGaussianBlur stdDeviation="3" />
            </filter>
            <filter id={`${id}-glow`} x="-40%" y="-60%" width="180%" height="220%">
              <feGaussianBlur stdDeviation="6" />
            </filter>
          </defs>
          {/* The patch: visor-black over the baked eyes and smile, feathered so it has no edge. */}
          <rect x="178" y="126" width="214" height="88" rx="44" fill="#0d0b09" filter={`url(#${id}-soft)`} />
          <g className="m3d-eyes">
            {/* Happy arcs, like the ones painted in the picture, so it is the same Magix. */}
            <g className="m3d-eye">
              <path d="M197 172C200 146 238 146 241 172" fill="none" stroke="#ffc94a" strokeWidth="14" strokeLinecap="round" opacity="0.8" filter={`url(#${id}-glow)`} />
              <path d="M197 172C200 146 238 146 241 172" fill="none" stroke="#ffe08a" strokeWidth="9" strokeLinecap="round" />
            </g>
            <g className="m3d-eye">
              <path d="M322 180C325 154 363 154 367 180" fill="none" stroke="#ffc94a" strokeWidth="14" strokeLinecap="round" opacity="0.8" filter={`url(#${id}-glow)`} />
              <path d="M322 180C325 154 363 154 367 180" fill="none" stroke="#ffe08a" strokeWidth="9" strokeLinecap="round" />
            </g>
            <path d="M267 186q14 12 28 0" fill="none" stroke="#ffd86b" strokeWidth="4.5" strokeLinecap="round" />
          </g>
        </svg>
      </div>
      <i className="m3d-shadow" />
    </div>
  );
}
